import { collectA2uiComponents } from './a2ui-components';
import { SSEResponseTypes } from '../../../models/websockets.types';
import type {
  A2uiSurfaceStatus,
  ApprovalGrantScope,
  MessageA2uiApproval,
  MessageA2uiSubmission,
  MessageA2uiSurface,
} from '../types/chat';

const A2UI_STATUSES: A2uiSurfaceStatus[] = [
  'active',
  'answered',
  'approved',
  'declined',
  'expired',
  'skipped',
];

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function parseOptionalString(value: unknown): string | undefined {
  if (value == null) return undefined;
  const text = String(value).trim();
  return text || undefined;
}

function parseStatus(value: unknown): A2uiSurfaceStatus {
  const status = parseOptionalString(value) as A2uiSurfaceStatus | undefined;
  return status && A2UI_STATUSES.includes(status) ? status : 'active';
}

const APPROVAL_GRANT_SCOPES: ApprovalGrantScope[] = [
  'once',
  'chat',
  'always_action',
  'always_tool',
];

function parseAllowedScopes(value: unknown): ApprovalGrantScope[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const scopes = value.filter((item): item is ApprovalGrantScope =>
    APPROVAL_GRANT_SCOPES.includes(item as ApprovalGrantScope)
  );
  return scopes.length ? scopes : undefined;
}

function parseOptionalNumber(value: unknown): number | undefined {
  if (value == null) return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
}

function parseSpecMessages(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is Record<string, unknown> => !!asRecord(item));
}

/** WebSocket A2UI_MESSAGE payload (wire contract §1). */
export function parseA2uiSurfaceFromStreamPayload(
  data: unknown
): { surface: MessageA2uiSurface; kind: 'surface' | 'update' } | null {
  const o = asRecord(data);
  if (!o) return null;

  const surfaceId = parseOptionalString(o.surfaceId ?? o.surface_id);
  if (!surfaceId) return null;

  const kind = parseOptionalString(o.kind) === 'update' ? 'update' : 'surface';
  return {
    kind,
    surface: {
      surfaceId,
      initiatorUserId: parseOptionalString(o.initiatorUserId ?? o.initiator_user_id),
      version: parseOptionalString(o.version) ?? '0.9',
      purpose: parseOptionalString(o.purpose) ?? 'generic',
      messages: parseSpecMessages(o.messages),
      status: 'active',
    },
  };
}

function parseApprovalRecord(o: Record<string, unknown>): MessageA2uiApproval | null {
  const approvalId = parseOptionalString(o.approvalId ?? o.approval_id ?? o.ApprovalId);
  if (!approvalId) return null;

  const toolArgs = asRecord(o.toolArgs ?? o.tool_args ?? o.ToolArgs);
  const allowedScopes = parseAllowedScopes(o.allowedScopes ?? o.allowed_scopes ?? o.AllowedScopes);
  const actionCount = parseOptionalNumber(
    o.toolActionCount ?? o.tool_action_count ?? o.ToolActionCount
  );
  return {
    approvalId,
    ...(allowedScopes && { allowedScopes }),
    ...(actionCount != null && { actionCount }),
    toolName: parseOptionalString(o.toolName ?? o.tool_name ?? o.ToolName),
    ...(toolArgs && { toolArgs }),
    agentId: parseOptionalString(o.agentId ?? o.agent_id ?? o.AgentId),
    subagentName: parseOptionalString(o.subagentName ?? o.subagent_name ?? o.SubagentName),
    initiatorUserId: parseOptionalString(
      o.initiatorUserId ?? o.initiator_user_id ?? o.InitiatorUserId
    ),
    expiresAt: parseOptionalString(o.expiresAt ?? o.expires_at ?? o.ExpiresAt),
  };
}

/** WebSocket APPROVAL_REQUIRED payload → approval surface (wire contract §3). */
export function parseApprovalRequiredFromStreamPayload(data: unknown): MessageA2uiSurface | null {
  const o = asRecord(data);
  if (!o) return null;

  const approval = parseApprovalRecord(o);
  if (!approval) return null;

  const surfacePayload = asRecord(o.surface);
  const surfaceId =
    parseOptionalString(surfacePayload?.surfaceId ?? surfacePayload?.surface_id) ??
    approval.approvalId;

  return {
    surfaceId,
    version: parseOptionalString(surfacePayload?.version) ?? '0.9',
    purpose: parseOptionalString(surfacePayload?.purpose) ?? 'approval',
    messages: parseSpecMessages(surfacePayload?.messages),
    status: 'active',
    approval,
  };
}

/** WebSocket APPROVAL_RESOLVED payload (wire contract §3). */
export function parseApprovalResolvedFromStreamPayload(data: unknown): {
  approvalId: string;
  decision: 'approved' | 'declined';
  resolvedBy?: string;
} | null {
  const o = asRecord(data);
  if (!o) return null;

  const approvalId = parseOptionalString(o.approvalId ?? o.approval_id);
  if (!approvalId) return null;

  return {
    approvalId,
    decision: parseOptionalString(o.decision) === 'approved' ? 'approved' : 'declined',
    resolvedBy: parseOptionalString(o.resolvedBy ?? o.resolved_by),
  };
}

export function upsertMessageA2uiSurface(
  surfaces: MessageA2uiSurface[] | undefined,
  next: MessageA2uiSurface,
  kind: 'surface' | 'update'
): MessageA2uiSurface[] {
  const list = surfaces ? [...surfaces] : [];
  const index = list.findIndex((surface) => surface.surfaceId === next.surfaceId);
  if (index === -1) {
    return [...list, next];
  }
  const existing = list[index];
  list[index] = {
    ...existing,
    ...next,
    messages: kind === 'update' ? [...existing.messages, ...next.messages] : next.messages,
    // Keep the original insert point so later answer text / steps stay below the card.
    contentOffset: existing.contentOffset ?? next.contentOffset,
    statusEntryCutoff: existing.statusEntryCutoff ?? next.statusEntryCutoff,
    ...(existing.approval && !next.approval && { approval: existing.approval }),
  };
  return list;
}

export function resolveMessageA2uiApproval(
  surfaces: MessageA2uiSurface[] | undefined,
  resolved: { approvalId: string; decision: 'approved' | 'declined'; resolvedBy?: string }
): MessageA2uiSurface[] {
  return (surfaces ?? []).map((surface) => {
    if (surface.approval?.approvalId !== resolved.approvalId) return surface;
    return {
      ...surface,
      status: resolved.decision,
      approval: {
        ...surface.approval,
        decision: resolved.decision,
        resolvedBy: resolved.resolvedBy,
      },
    };
  });
}

/** Split answer text so the A2UI/approval card sits between before/after. */
export function getA2uiSplitOffset(
  surfaces: MessageA2uiSurface[] | undefined,
  contentLength: number
): number {
  let min: number | undefined;
  for (const surface of surfaces ?? []) {
    if (typeof surface.contentOffset !== 'number') continue;
    min = min == null ? surface.contentOffset : Math.min(min, surface.contentOffset);
  }
  if (min == null) return contentLength;
  return Math.min(Math.max(0, min), contentLength);
}

/** Earliest status-entry index when an A2UI/approval surface arrived (Steps above the card). */
export function getA2uiStatusEntryCutoff(
  surfaces: MessageA2uiSurface[] | undefined
): number | undefined {
  let min: number | undefined;
  for (const surface of surfaces ?? []) {
    if (typeof surface.statusEntryCutoff !== 'number') continue;
    min = min == null ? surface.statusEntryCutoff : Math.min(min, surface.statusEntryCutoff);
  }
  return min;
}

export type A2uiSurfaceStreamPin = {
  contentOffset?: number;
  statusEntryCutoff?: number;
};

function withStreamPin(
  surface: MessageA2uiSurface,
  pin?: A2uiSurfaceStreamPin
): MessageA2uiSurface {
  if (!pin) return surface;
  return {
    ...surface,
    ...(typeof pin.contentOffset === 'number' && { contentOffset: pin.contentOffset }),
    ...(typeof pin.statusEntryCutoff === 'number' && {
      statusEntryCutoff: pin.statusEntryCutoff,
    }),
  };
}

/**
 * Builds the a2uiSurfaces updater for one A2UI/approval stream chunk so the
 * chat store applies live and reconnect-replay chunks identically.
 * Pin fields keep later answer text / Steps below the card.
 */
export function buildA2uiSurfacesUpdater(
  type: SSEResponseTypes,
  data: unknown,
  pin?: A2uiSurfaceStreamPin
): ((surfaces: MessageA2uiSurface[] | undefined) => MessageA2uiSurface[]) | null {
  if (type === SSEResponseTypes.A2UI_MESSAGE) {
    const parsed = parseA2uiSurfaceFromStreamPayload(data);
    if (!parsed) return null;
    const surface = parsed.kind === 'surface' ? withStreamPin(parsed.surface, pin) : parsed.surface;
    return (surfaces) => upsertMessageA2uiSurface(surfaces, surface, parsed.kind);
  }
  if (type === SSEResponseTypes.APPROVAL_REQUIRED) {
    const surface = parseApprovalRequiredFromStreamPayload(data);
    if (!surface) return null;
    return (surfaces) => upsertMessageA2uiSurface(surfaces, withStreamPin(surface, pin), 'surface');
  }
  if (type === SSEResponseTypes.APPROVAL_RESOLVED) {
    const resolved = parseApprovalResolvedFromStreamPayload(data);
    if (!resolved) return null;
    return (surfaces) => resolveMessageA2uiApproval(surfaces, resolved);
  }
  return null;
}

function metadataA2ui(metadata: unknown): Record<string, unknown> | null {
  const meta = asRecord(metadata);
  if (!meta) return null;
  // BE pydantic camel/Pascal aliasing of `a2ui` yields "a2Ui"/"A2Ui" on the wire.
  return asRecord(meta.a2ui ?? meta.a2Ui ?? meta.A2Ui ?? meta.A2UI);
}

/**
 * surfaceId → submitted answer, indexed from raw history messages. A surface
 * submit posts a later user message whose `metadata.a2ui` carries the flat
 * `{surfaceId, action, selections, instructions}` shape (wire contract §4).
 */
export function hasPendingA2uiApproval(surfaces?: MessageA2uiSurface[] | null): boolean {
  const now = Date.now();
  return (surfaces ?? []).some((s) => {
    if (!s.approval || s.status !== 'active') return false;
    const expiry = s.approval.expiresAt ? Date.parse(s.approval.expiresAt) : NaN;
    return Number.isNaN(expiry) || expiry > now;
  });
}

export function collectA2uiSubmissions(messages: unknown[]): Map<string, MessageA2uiSubmission> {
  const submissions = new Map<string, MessageA2uiSubmission>();
  for (const message of messages) {
    const record = asRecord(message);
    const a2ui = metadataA2ui(record?.metadata ?? record?.Metadata);
    if (!a2ui) continue;
    const surfaceId = parseOptionalString(a2ui.surfaceId ?? a2ui.surface_id ?? a2ui.SurfaceId);
    if (!surfaceId) continue;
    const selections = asRecord(a2ui.selections ?? a2ui.Selections) ?? {};
    const instructions = parseOptionalString(a2ui.instructions ?? a2ui.Instructions);
    submissions.set(surfaceId, { selections, ...(instructions && { instructions }) });
  }
  return submissions;
}

const APPROVAL_LEDGER_STATUS: Partial<Record<string, A2uiSurfaceStatus>> = {
  pending: 'active',
  approved: 'approved',
  declined: 'declined',
  expired: 'expired',
};

export interface A2uiApprovalLedgerEntry {
  approval: MessageA2uiApproval;
  status: A2uiSurfaceStatus;
}

/**
 * surfaceId → persisted HIL approval, indexed from raw history messages. The
 * server keeps the approval ledger on `metadata.agentRun.approvals` (not on the
 * surface record), so history rehydration reattaches approvals from here.
 */
export function collectA2uiApprovals(messages: unknown[]): Map<string, A2uiApprovalLedgerEntry> {
  const approvals = new Map<string, A2uiApprovalLedgerEntry>();
  for (const message of messages) {
    const record = asRecord(message);
    const meta = asRecord(record?.metadata ?? record?.Metadata);
    const agentRun = asRecord(meta?.agentRun ?? meta?.agent_run ?? meta?.AgentRun);
    const entries = agentRun?.approvals ?? agentRun?.Approvals;
    if (!Array.isArray(entries)) continue;
    for (const item of entries) {
      const o = asRecord(item);
      const approval = o && parseApprovalRecord(o);
      if (!o || !approval) continue;
      const surfaceId =
        parseOptionalString(o.surfaceId ?? o.surface_id ?? o.SurfaceId) ?? approval.approvalId;
      const status =
        APPROVAL_LEDGER_STATUS[parseOptionalString(o.status ?? o.Status)?.toLowerCase() ?? ''] ??
        'active';
      const resolvedBy = parseOptionalString(o.resolvedBy ?? o.resolved_by ?? o.ResolvedBy);
      approvals.set(surfaceId, {
        status,
        approval: {
          ...approval,
          ...((status === 'approved' || status === 'declined') && {
            decision: status,
            ...(resolvedBy && { resolvedBy }),
          }),
        },
      });
    }
  }
  return approvals;
}

/** Data-model binding path (`{path: ...}` under value/values/selections/text) of a component def. */
const BINDING_PROPS = ['value', 'values', 'selections', 'text'];

function bindingPath(component: Record<string, unknown>): string | undefined {
  for (const prop of BINDING_PROPS) {
    const binding = asRecord(component[prop]);
    const path = binding && parseOptionalString(binding.path);
    if (path) return path;
  }
  return undefined;
}

function findComponent(
  surface: MessageA2uiSurface,
  predicate: (component: Record<string, unknown>) => boolean
): Record<string, unknown> | undefined {
  for (const message of [...surface.messages].reverse()) {
    const components = asRecord(message.updateComponents)?.components;
    if (!Array.isArray(components)) continue;
    for (const entry of components) {
      const component = asRecord(entry);
      if (component && predicate(component)) return component;
    }
  }
  return undefined;
}

/**
 * Marks a surface answered and replays the submitted values into its data model
 * via synthesized `updateDataModel` messages (same single-key envelope the agent
 * emits), so the renderer shows the chosen values after a reload.
 */
function applyA2uiSubmission(
  surface: MessageA2uiSurface,
  submission: MessageA2uiSubmission
): MessageA2uiSurface {
  const updates: Record<string, unknown>[] = [];
  const push = (path: string | undefined, value: unknown) => {
    if (path) updates.push({ updateDataModel: { surfaceId: surface.surfaceId, path, value } });
  };
  let components;
  try {
    components = collectA2uiComponents(surface);
  } catch {
    // Preserve corrupt historical payloads for the renderer’s visible fallback.
    return { ...surface, status: 'answered', submission };
  }
  const context = [...components.values()]
    .filter((c) => c.component === 'Button')
    .reduce(
      (all, c) => ({ ...all, ...(c.action?.event?.context ?? {}) }),
      {} as Record<string, any>
    );
  for (const [componentId, value] of Object.entries(submission.selections)) {
    if (value == null) continue;
    const component = findComponent(surface, (entry) => entry.id === componentId);
    push(context[componentId]?.path ?? (component && bindingPath(component)), value);
  }
  if (submission.instructions) {
    const instructionsField = findComponent(
      surface,
      (entry) => entry.id === 'instructions' && entry.component === 'TextField'
    );
    push(
      context.instructions?.path ?? (instructionsField && bindingPath(instructionsField)),
      submission.instructions
    );
  }
  return {
    ...surface,
    status: 'answered',
    submission,
    messages: [...surface.messages, ...updates],
  };
}

function parseSurfaceRecord(item: unknown): MessageA2uiSurface | null {
  const o = asRecord(item);
  if (!o) return null;

  const surfaceId = parseOptionalString(o.surfaceId ?? o.surface_id ?? o.SurfaceId);
  if (!surfaceId) return null;

  const approvalRecord = asRecord(o.approval ?? o.Approval);
  const approval = approvalRecord ? parseApprovalRecord(approvalRecord) : null;
  return {
    surfaceId,
    initiatorUserId: parseOptionalString(
      o.initiatorUserId ?? o.initiator_user_id ?? o.InitiatorUserId
    ),
    version: parseOptionalString(o.version ?? o.Version) ?? '0.9',
    purpose: parseOptionalString(o.purpose ?? o.Purpose) ?? 'generic',
    messages: parseSpecMessages(o.messages ?? o.Messages),
    status: parseStatus(o.status ?? o.Status),
    ...(approval && { approval }),
  };
}

/**
 * Reads persisted surfaces from message `metadata.a2ui` (wire contract §5).
 * `submissions` (from {@link collectA2uiSubmissions} over the full history) marks
 * submitted surfaces answered and restores their form values; `approvals` (from
 * {@link collectA2uiApprovals}) reattaches the HIL approval record and its
 * ledger status so approval surfaces keep their dedicated card after reload.
 */
export function parseA2uiSurfacesFromMetadata(
  metadata: unknown,
  submissions?: Map<string, MessageA2uiSubmission>,
  approvals?: Map<string, A2uiApprovalLedgerEntry>
): MessageA2uiSurface[] | undefined {
  const a2ui = metadataA2ui(metadata);
  const rawSurfaces = a2ui?.surfaces ?? a2ui?.Surfaces;
  if (!Array.isArray(rawSurfaces) || rawSurfaces.length === 0) {
    return undefined;
  }

  const parsed: MessageA2uiSurface[] = [];
  for (const item of rawSurfaces) {
    let surface = parseSurfaceRecord(item);
    if (!surface) continue;
    const ledger = approvals?.get(surface.surfaceId);
    if (ledger) {
      surface = { ...surface, status: ledger.status, approval: ledger.approval };
    }
    const submission = submissions?.get(surface.surfaceId);
    parsed.push(
      submission && !surface.approval ? applyA2uiSubmission(surface, submission) : surface
    );
  }
  return parsed.length > 0 ? parsed : undefined;
}
